/**
 * Data-driven wizard configs for the six document wizards.
 *
 * Phase 20 (Refactor 3). Companion to `../wizard-schemas/` (Refactor 2,
 * the per-step Zod schemas) and `@thesitebook/strings` (Refactor 4, the
 * copy table).
 *
 * Each renderer (mobile today, web later) iterates over
 * `WIZARD_CONFIGS[<kind>].steps` and draws every step either via the
 * generic field renderer (`step.fields`) or by looking up
 * `step.customRenderer` in a screen-side renderer map. AI section-fill
 * is declared once per step and the renderer wires
 * `AISectionSuggestionButton` automatically when present.
 *
 * Steps with bespoke UI live as custom renderers because they can't be
 * expressed as a flat list of typed fields without inventing a much
 * larger schema:
 *
 *   - hazard / control / attendee / topic / key-points builders
 *   - preview cards (use the existing `WizardPreview` component shape)
 *
 * Renderer maps belong with the screens, not contracts — contracts only
 * knows the string identifier.
 *
 * See `./types.ts` for the `WizardConfig` / `WizardStepConfig` /
 * `WizardFieldDefinition` shapes.
 */
import type { WizardKind } from "../wizard-schemas/index";
import type { WizardConfig } from "./types";

import { RAMS_WIZARD_CONFIG } from "./rams";
import { CPP_WIZARD_CONFIG } from "./cpp";
import { LIGHTWEIGHT_CPP_WIZARD_CONFIG } from "./lightweight-cpp";
import { SITE_INDUCTION_WIZARD_CONFIG } from "./site-induction";
import { EMERGENCY_PLAN_WIZARD_CONFIG } from "./emergency-plan";
import { TOOLBOX_TALK_WIZARD_CONFIG } from "./toolbox-talk";

export * from "./types";
export { RAMS_WIZARD_CONFIG } from "./rams";
export { CPP_WIZARD_CONFIG } from "./cpp";
export { LIGHTWEIGHT_CPP_WIZARD_CONFIG } from "./lightweight-cpp";
export { SITE_INDUCTION_WIZARD_CONFIG } from "./site-induction";
export { EMERGENCY_PLAN_WIZARD_CONFIG } from "./emergency-plan";
export { TOOLBOX_TALK_WIZARD_CONFIG } from "./toolbox-talk";

export const WIZARD_CONFIGS: Readonly<Record<WizardKind, WizardConfig>> = {
  RAMS: RAMS_WIZARD_CONFIG,
  CPP: CPP_WIZARD_CONFIG,
  CPP_LIGHTWEIGHT: LIGHTWEIGHT_CPP_WIZARD_CONFIG,
  SITE_INDUCTION: SITE_INDUCTION_WIZARD_CONFIG,
  EMERGENCY_PLAN: EMERGENCY_PLAN_WIZARD_CONFIG,
  TOOLBOX_TALK: TOOLBOX_TALK_WIZARD_CONFIG,
};
